import { useEffect, useRef, useState } from 'react';

import { composeAdminMemberSessionRows } from './composeAdminMemberSessionRows';

import type {
  AdminMemberSessionDiagnostic,
  AdminMemberSessionRow,
} from './composeAdminMemberSessionRows';
import type { AdminAppointment } from '../appointments/types';
import type { Session } from '@/types/session';

import { useGetMomentsMemberScopeAppointmentsQuery } from '@/store/api/momentsApi';
import { useGetAllSessionsQuery } from '@/store/api/sessionApi';

export interface UseAdminMemberSessionsOptions {
  memberId: string;
  viewerId: string;
  viewerRoles: string[];
  timeZone: string;
  enabled?: boolean;
}

export interface AdminMemberSessionsResult {
  rows: AdminMemberSessionRow[];
  diagnostics: AdminMemberSessionDiagnostic[];
  status: 'loading' | 'ready' | 'partial' | 'error';
  isRefreshing: boolean;
  errorSources: string[];
  scopeKey: string;
  refetch: () => void;
}

const isDenied = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  (error.status === 401 || error.status === 403 || error.status === 428);

const validId = (id: string): boolean =>
  id.trim().length > 0 &&
  !/^0+$/u.test(id) &&
  id !== '00000000-0000-0000-0000-000000000000';

export function useAdminMemberSessions({
  memberId,
  viewerId,
  viewerRoles,
  timeZone,
  enabled = true,
}: UseAdminMemberSessionsOptions): AdminMemberSessionsResult {
  const skip =
    !enabled ||
    !validId(memberId) ||
    !validId(viewerId) ||
    !viewerRoles.some((role) => role === 'admin' || role === 'super_admin');
  const mountId = useRef<string>();
  if (mountId.current === undefined) mountId.current = crypto.randomUUID();
  const identity = JSON.stringify([
    memberId,
    viewerId,
    [...new Set(viewerRoles)].sort(),
    timeZone,
    enabled,
  ]);
  const [scope, setScope] = useState({ generation: 0, identity });
  const generation = scope.generation + (scope.identity !== identity ? 1 : 0);
  // Rotate on every transition, including A -> B -> A, before selecting RTK data.
  if (scope.identity !== identity) setScope({ generation, identity });
  const scopeKey = JSON.stringify([mountId.current, identity, generation]);
  const rails = useGetAllSessionsQuery(
    { cacheScope: scopeKey, historyRead: true, userId: memberId },
    { skip },
  );
  const native = useGetMomentsMemberScopeAppointmentsQuery(
    { cacheScope: scopeKey, memberId, readMode: 'history' },
    { skip },
  );
  const previous = useRef<{
    scopeKey: string;
    settled?: boolean;
    sessions?: Session[];
    appointments?: AdminAppointment[];
    denial?: {
      railsRequestId?: string;
      nativeRequestId?: string;
      errorSources: string[];
    };
  }>({ scopeKey });
  const retained =
    previous.current.scopeKey === scopeKey ? previous.current : undefined;
  const terminalSources = [
    rails.isError && isDenied(rails.error) ? 'rails' : '',
    native.isError && isDenied(native.error) ? 'native' : '',
  ].filter(Boolean);
  const denial = retained?.denial;
  const reauthorized =
    denial &&
    rails.isSuccess &&
    native.isSuccess &&
    !rails.isFetching &&
    !native.isFetching &&
    rails.requestId !== denial.railsRequestId &&
    native.requestId !== denial.nativeRequestId;
  const suppressed =
    terminalSources.length > 0 || Boolean(denial && !reauthorized);
  const sessions = rails.currentData
    ? Object.values(rails.currentData.items).flat()
    : retained?.sessions;
  const appointments =
    native.currentData?.appointments ?? retained?.appointments;
  useEffect(() => {
    if (skip) {
      previous.current = { scopeKey };
      return;
    }
    if (previous.current.scopeKey !== scopeKey) previous.current = { scopeKey };
    if (
      !rails.isFetching &&
      !native.isFetching &&
      !rails.isUninitialized &&
      !native.isUninitialized
    )
      previous.current.settled = true;
    if (terminalSources.length > 0) {
      previous.current = {
        denial: {
          errorSources: terminalSources,
          nativeRequestId: native.requestId,
          railsRequestId: rails.requestId,
        },
        scopeKey,
      };
      return;
    }
    if (suppressed) return;
    delete previous.current.denial;
    if (rails.isSuccess && !rails.isFetching)
      previous.current.sessions = Object.values(rails.currentData.items).flat();
    if (native.isSuccess && !native.isFetching)
      previous.current.appointments = native.currentData.appointments;
  }, [
    skip,
    scopeKey,
    rails.currentData,
    rails.isSuccess,
    rails.isFetching,
    rails.isUninitialized,
    rails.error,
    rails.isError,
    rails.requestId,
    native.currentData,
    native.isSuccess,
    native.isFetching,
    native.isUninitialized,
    native.error,
    native.isError,
    native.requestId,
    suppressed,
  ]);
  const errorSources = [
    ...new Set([
      ...(suppressed ? (denial?.errorSources ?? terminalSources) : []),
      ...(rails.isError || (retained?.settled && sessions === undefined)
        ? ['rails']
        : []),
      ...(native.isError || (retained?.settled && appointments === undefined)
        ? ['native']
        : []),
    ]),
  ];
  const initialLoading =
    !retained?.settled &&
    ((sessions === undefined && !rails.isError) ||
      (appointments === undefined && !native.isError));
  const composed = composeAdminMemberSessionRows({
    appointments: skip || suppressed ? [] : (appointments ?? []),
    memberId,
    sessions: skip || suppressed ? [] : (sessions ?? []),
  });
  if (composed.diagnostics.length > 0) errorSources.push('diagnostics');
  let status: AdminMemberSessionsResult['status'] = 'ready';
  if (!skip) {
    if (suppressed) status = 'error';
    else if (initialLoading) status = 'loading';
    else if (errorSources.length > 0)
      status = sessions || appointments ? 'partial' : 'error';
  }
  return {
    ...composed,
    errorSources: skip ? [] : errorSources,
    isRefreshing:
      !skip &&
      (suppressed || !initialLoading) &&
      (rails.isFetching || native.isFetching),
    refetch: () => {
      if (skip) return;
      if (!rails.isUninitialized) rails.refetch();
      if (!native.isUninitialized) native.refetch();
    },
    scopeKey,
    status,
  };
}
